import { AppShell, Divider, MantineProvider, ScrollArea } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Notifications } from "@mantine/notifications";
import React, { useLayoutEffect } from "react";
import { Outlet, useMatches, useParams } from "react-router-dom";
import VoiceekLogo from "@/assets/Voiceek_logo.svg?react";
import UserMenu from "@/components/admin/header/UserMenu";
import NavigationMenu from "@/components/admin/sidenav/NavigationMenu";
import WorkspaceSwitch from "@/components/ui/WorkspaceSwitch";
import navConfig from "@/configs/navigation.config/admin";
import { routes } from "@/configs/router.config/routes";
import useAuth from "@/hooks/useAuth";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { theme } from "@/theme";

const AdminLayout: React.FC = () => {
  const { workspaceId } = useParams();
  const { authUser } = useAuth();
  const [opened] = useDisclosure();
  const matches = useMatches();
  const { sessionCurrentWorkspaceId, setWorkspaceId } = useWorkspaces();

  useLayoutEffect(() => {
    // AdminLayoutが読み込まれてるタイミングでbase layerのcssを読み込む
    // UI側に戻る動線はロゴクリックしかなく、aタグ遷移のため読み込んだcssは消える
    const loadCSS = async () => {
      await import("@mantine/core/styles.css");
      await import("@mantine/dates/styles.css");
      await import("@/admin.css");
    };
    loadCSS();

    // URLにあるworkspaceIdをlocalStorageに保存する
    if (!workspaceId) return;
    setWorkspaceId(workspaceId);
  }, [workspaceId]);

  if (!authUser) return <></>;
  if (authUser.role === "user") {
    window.location.assign("/");
    return;
  }

  const handle = matches[matches.length - 1].handle;
  const hideSideNav = typeof handle === "object"
    && handle !== null
    && "hideSideNav" in handle
    && handle.hideSideNav;

  const isSuperAdmin = authUser.role === "superadmin";
  const links = navConfig(sessionCurrentWorkspaceId, isSuperAdmin).map((item) => (
    <NavigationMenu {...item} key={item.label} />
  ));

  const sidenav = hideSideNav ? <></> : (
    <AppShell.Navbar p="xs" w="260px">
      <AppShell.Section grow my="md" component={ScrollArea}>
        {links}
      </AppShell.Section>
      <AppShell.Section>
        <Divider my="md" />
      </AppShell.Section>
    </AppShell.Navbar>
  );

  return (
    <MantineProvider theme={theme}>
      <Notifications position="top-right" />
      <AppShell
        header={{ height: 70 }}
        navbar={{
          width: hideSideNav ? 0 : 270,
          breakpoint: "sm",
          collapsed: { mobile: !opened },
        }}
        padding="md"
      >
        <AppShell.Header style={{ minWidth: "460px" }} py={1}>
          <div className="flex h-full justify-between items-center px-5">
            <a href={routes.top(sessionCurrentWorkspaceId)}>
              <VoiceekLogo />
            </a>
            <div className="flex gap-5">
              <WorkspaceSwitch />
              <UserMenu />
            </div>
          </div>
        </AppShell.Header>

        {sidenav}

        <AppShell.Main style={{ minHeight: "auto" }}>
          <Outlet />
        </AppShell.Main>
      </AppShell>
    </MantineProvider>
  );
};

export default AdminLayout;
